import PageLoader from '../../components/shared/PageLoader';
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { notifyError, notifyWarning } from "../../helper/notify";
import { isGroupOnlyModule } from "../../helper/libs/is-group-only-module";
import { logout } from "../../redux/slices/authSlice";
import { isCustomerSide } from "../../utils/accountSide";
import { getPendingSignup } from "../../utils/pendingSignup";
import { loginRoute, dashboardRoute, subscriptionRoute } from "../../utils/appRoutes";

const CustomerProtectedRoute = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    role,
    side,
    modules,
    subscription,
    isLoading
  } = useSelector((state) => state.auth);

  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState("");

  useEffect(() => {
    console.log('[LastPage] CustomerProtectedRoute effect run — path:', location.pathname,
      'isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'role:', role);

    if (isLoading) {
      console.log('[LastPage] CustomerProtectedRoute: still isLoading, bailing out for now');
      return;
    }

    if (!isAuthenticated) {
      console.log('[LastPage] CustomerProtectedRoute: not authenticated, will render <Navigate to="/login">');
      setIsChecking(false);
      return;
    }

    const currentPath = location.pathname;

    // 1. STRICT CHECK: Only Customer can access
    if (!isCustomerSide({ side, role })) {
      notifyError("Access denied. This area is for customers only.");
      dispatch(logout()); // Force logout
      setRedirectPath(loginRoute(true));
      setShouldRedirect(true);
      setIsChecking(false);
      return;
    }

    // 2. The plan page is open to every customer — and a customer with no plan and no group yet (just signed up) can
    //    only be there: the Free plan is one of the plans on it. (There is no free trial any more.)
    if (currentPath === subscriptionRoute()) {
      setIsChecking(false);
      return;
    }
    // no plan AND no module the account can really open → only the plan page (a plan decides the modules)
    if (!subscription && !(modules || []).some((m) => (m.permissions || []).length > 0)) {
      notifyWarning("Please choose a plan to continue.");
      setRedirectPath(subscriptionRoute());
      setShouldRedirect(true);
      setIsChecking(false);
      return;
    }

    // 3. Check if current path is accessible
    // Always allow dashboard
    if (currentPath === dashboardRoute(false)) {
      setIsChecking(false);
      return;
    }

    // Check if current path matches any module
    const hasAccess = modules.some(module => {
      if (!module.route_path) return false;
      // A group-only module has no page — its placeholder URL is not a page.
      if (isGroupOnlyModule(module, modules)) return false;

      // Exact match
      if (currentPath === module.route_path) return true;

      // Check for sub-paths
      if (module.route_path !== "/" && currentPath.startsWith(module.route_path)) {
        return true;
      }

      return false;
    });

    console.log('[LastPage] CustomerProtectedRoute: checking access for', currentPath,
      '— hasAccess =', hasAccess, '— modules route_paths =', modules.map(m => m.route_path));

    if (!hasAccess) {
      notifyError("You don't have permission to access this page.");

      // Find first accessible module to redirect to
      const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0));
      const firstModule = sortedModules.find(m => m.route_path && !isGroupOnlyModule(m, modules));
      const redirect = firstModule?.route_path || dashboardRoute(false);

      console.log('[LastPage] CustomerProtectedRoute: bouncing away from', currentPath, 'to', redirect, '— this overrides any last-visited-page redirect!');
      setRedirectPath(redirect);
      setShouldRedirect(true);
    }

    setIsChecking(false);
  }, [isAuthenticated, role, side, modules, location.pathname, subscription, dispatch, isLoading]);

  // Show loading while checking
  if (isLoading || isChecking) {
    return (
      <PageLoader fullScreen />
    );
  }

  // A visitor in the middle of signing up (details kept, no account yet) may be on the plan page — that is where the account is made
  const signupFlow = !isAuthenticated && location.pathname === subscriptionRoute() && !!getPendingSignup();
  if (signupFlow) return children;

  // If not authenticated, redirect to customer login
  if (!isAuthenticated) {
    return <Navigate to={loginRoute(false)} state={{ from: location }} replace />;
  }

  // If should redirect, navigate
  if (shouldRedirect && redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Render the children if all checks pass
  return children;
};

export default CustomerProtectedRoute;